import Close16 from '@carbon/icons-react/es/close/16';
import { Col, Form, Row } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId } from 'api/models';
import { DuplicateTaskProps } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { Radio } from 'components/atoms/radio';
import { Select } from 'components/atoms/select';
import { useProjectDetail } from 'data/projects/hooks';
import { Task } from 'data/tasks';
import { useTranslation } from 'hooks/use-translation';
import { useDuplicateTaskMutation } from 'queries';
import { DuplicateTaskConstants as Constant } from './constant';
import { ProjectPhaseComponent } from './project-phase';
import {
  ButtonGroup,
  CheckBox,
  DividerLine,
  DuplicateTaskForm,
  Input,
  InputContainer,
  ParaContainer,
  PrefixInput,
  RadioGroup,
  StyledFormItem,
  StyledRow,
  SubHeadings,
  SubTitle,
  SuffixInput,
} from './style';
import { ItemsToDuplicate, ProjectPhaseType } from './types';

interface Props {
  task: Task;
  closeHandler: () => void;
  onCancel?: () => void;
  className?: string;
  fromSubTaskPane?: boolean;
}

export const DuplicateTaskDrawer: FC<Props> = ({
  task,
  closeHandler,
  onCancel,
  className,
  fromSubTaskPane,
}) => {
  const noProjectPhase = useTranslation(
    'task.duplicate_task.phase.dont_associate',
  );
  const { data: projectDetail } = useProjectDetail(ProjectId(task.project));
  const projectPhase = task?.projectPhase?.projectPhaseName
    ? task?.projectPhase?.projectPhaseName
    : noProjectPhase;
  const projectPhaseId = task?.projectPhase?.projectPhaseName
    ? task?.projectPhase?.projectPhaseId
    : -1;
  const projectPhaseOptions = projectDetail?.projectPhases?.reduce(
    (result, item) =>
      result.concat({
        value: item.projectPhaseId,
        label: item.projectPhaseName,
      }),
    [] as ProjectPhaseType[],
  );
  const checkBoxInitialValues: string[] = [
    ItemsToDuplicate.Description,
    ItemsToDuplicate.Attachments,
    ItemsToDuplicate.TaskDates,
    ItemsToDuplicate.SubTasks,
    ItemsToDuplicate.Assignees,
    ItemsToDuplicate.TaskFields,
  ];

  const [form] = Form.useForm();
  const { mutate, isLoading: isDuplicateInProgress } = useDuplicateTaskMutation(
    ProjectId(task.project),
  );

  const [duplicateCount, setDuplicateCount] = useState<any>(1);
  const [duplicateAllItems, setDuplicateAllItems] = useState<boolean>(true);
  const selectedProjectPhase = {
    label: projectPhase,
    value: projectPhaseId,
  };
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const duplicateTaskHandler = () => {
    const formInstance = form.getFieldsValue();
    const taskNames = [...Array(duplicateCount)].reduce(
      (result, _, index) =>
        result.concat(
          formInstance[`prefix-${index}`]
            ? `${formInstance[`prefix-${index}`]} ${
                formInstance[`suffix-${index}`]
              }`
            : formInstance[`suffix-${index}`],
        ),
      [] as string[],
    );
    const payload = {
      duplicateCount,
      baseTaskId: task.taskId,
      itemsToDuplicate: formInstance.itemsToDuplicate,
      projectPhaseId: formInstance.selectedProjectPhase.value,
      taskNames,
    };

    mutate(payload as DuplicateTaskProps, {
      onSettled: () => {
        closeHandler();
      },
    });
  };
  const validateForm = () =>
    setButtonDisabled(
      [...Array(duplicateCount)].some(
        (_, index) => !form.getFieldValue(`suffix-${index}`),
      ),
    );
  const duplicateCountChangeHandler = (value) => {
    if (
      value &&
      Number.isInteger(value) &&
      value >= Constant.minimumDuplicateLimit &&
      value <= Constant.maximumDuplicateLimit
    ) {
      setDuplicateCount(value);
    }
  };
  const phaseChangeHandler = (value) => {
    form.setFieldsValue({
      ...form.getFieldsValue(),
      selectedProjectPhase: value,
    });
  };

  return (
    <CustomLayoutDrawer
      width={360}
      placement="right"
      closeIcon={<Close16 />}
      visible
      mask
      destroyOnClose
      className={className}
      onClose={closeHandler}
      closable
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          <FormattedMessage id="task.duplicate_task" />
          <SubTitle>{task.taskName}</SubTitle>
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <DuplicateTaskForm form={form} layout="vertical">
            <Row>
              <Col span={24}>
                <InputContainer>
                  <Form.Item
                    label={<FormattedMessage id="task.duplicate_task" />}
                  >
                    <Input
                      min={Constant.minimumDuplicateLimit}
                      max={Constant.maximumDuplicateLimit}
                      value={duplicateCount}
                      onChange={duplicateCountChangeHandler}
                    />
                  </Form.Item>
                  <ParaContainer>
                    <FormattedMessage id="task.duplicate_task.no_of_times" />
                  </ParaContainer>
                </InputContainer>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Item
                  label={
                    <FormattedMessage id="task.duplicate_task.items.title" />
                  }
                  name="duplicateAll"
                  initialValue
                >
                  <RadioGroup
                    value={duplicateAllItems}
                    onChange={(e) => setDuplicateAllItems(e.target.value)}
                  >
                    <Radio value checked>
                      <FormattedMessage id="task.duplicate_task.items.everything" />
                    </Radio>
                    <Radio value={false}>
                      <FormattedMessage id="task.duplicate_task.items.choose" />
                    </Radio>
                  </RadioGroup>
                </Form.Item>
              </Col>
            </Row>
            <>
              {duplicateAllItems ? (
                <StyledFormItem
                  initialValue={checkBoxInitialValues}
                  name="itemsToDuplicate"
                  hideSpace
                />
              ) : (
                <Row align="middle">
                  <Col>
                    <DividerLine />
                    <Form.Item
                      initialValue={checkBoxInitialValues}
                      name="itemsToDuplicate"
                    >
                      <Checkbox.Group>
                        <Row>
                          <Col span={12}>
                            <CheckBox value={ItemsToDuplicate.Description}>
                              <FormattedMessage id="description" />
                            </CheckBox>
                          </Col>
                          <Col span={12}>
                            <CheckBox value={ItemsToDuplicate.Attachments}>
                              <FormattedMessage id="attachments" />
                            </CheckBox>
                          </Col>
                          <Col span={12}>
                            <CheckBox value={ItemsToDuplicate.TaskDates}>
                              <FormattedMessage id="task.dates" />
                            </CheckBox>
                          </Col>
                          <Col span={12}>
                            <CheckBox value={ItemsToDuplicate.SubTasks}>
                              <FormattedMessage id="sub_tasks" />
                            </CheckBox>
                          </Col>
                          <Col span={12}>
                            <CheckBox value={ItemsToDuplicate.Assignees}>
                              <FormattedMessage id="assignees" />
                            </CheckBox>
                          </Col>
                          <Col span={12}>
                            <CheckBox value={ItemsToDuplicate.TaskFields}>
                              <FormattedMessage id="task_fields" />
                            </CheckBox>
                          </Col>
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
            <Row>
              <Col span={24}>
                {projectPhaseOptions && !task.parentTask && !fromSubTaskPane ? (
                  <Form.Item
                    label={
                      <FormattedMessage id="task.duplicate_task.phase.select" />
                    }
                    name="selectedProjectPhase"
                    initialValue={selectedProjectPhase}
                  >
                    <Select
                      options={[
                        { label: noProjectPhase, value: -1 },
                        ...projectPhaseOptions,
                      ]}
                      components={{ Option: ProjectPhaseComponent }}
                      onChange={phaseChangeHandler}
                    />
                  </Form.Item>
                ) : (
                  <StyledFormItem
                    name="selectedProjectPhase"
                    initialValue={selectedProjectPhase}
                    hideSpace
                  />
                )}
              </Col>
            </Row>
            <Row gutter={0} wrap={false}>
              <Col flex="80px">
                <SubHeadings>
                  <FormattedMessage id="projects.phase.import.prefix" />
                </SubHeadings>
              </Col>
              <Col flex="auto">
                <SubHeadings>
                  <FormattedMessage id="task_name" />
                </SubHeadings>
              </Col>
            </Row>
            {[...Array(duplicateCount)].map((_, index) => {
              return (
                <StyledRow gutter={0} wrap={false}>
                  <Col flex="80px">
                    <StyledFormItem
                      name={`prefix-${index}`}
                      initialValue={index < 9 ? `0${index + 1}` : index + 1}
                    >
                      <PrefixInput autoComplete="off" />
                    </StyledFormItem>
                  </Col>
                  <Col flex="auto">
                    <StyledFormItem
                      name={`suffix-${index}`}
                      initialValue={task.taskName}
                      requiredMark
                    >
                      <SuffixInput onChange={validateForm} autoComplete="off" />
                    </StyledFormItem>
                  </Col>
                </StyledRow>
              );
            })}
          </DuplicateTaskForm>
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <ButtonGroup>
            <Button onClick={onCancel} data-cy="duplicate_task.cancel">
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              loading={isDuplicateInProgress}
              type="primary"
              onClick={duplicateTaskHandler}
              disabled={buttonDisabled}
              data-cy="duplicate_task.submit"
            >
              <FormattedMessage id="duplicate" />
            </Button>
          </ButtonGroup>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
